/* ============================================================
   BGC — bgc-why.js
   Motion + interaction for the Why Composite scenes owned by
   AGENT-WHY: bgc-safety / bgc-gas / bgc-wear / bgc-weigh.
   IIFE, guards on element existence, supports multiple section
   instances. Uses GSAP + ScrollTrigger when present (loaded
   globally in theme.liquid), else falls back to rAF / IO.
   ============================================================ */

(function () {
  "use strict";

  var reduced = document.documentElement.classList.contains("reduced");
  var hasGSAP = !!(window.gsap && window.ScrollTrigger);
  if (hasGSAP) {
    gsap.registerPlugin(ScrollTrigger);
  }

  /* ---------- bgc-gas — animate 100 -> 30 once on section entry, no pin, replays on re-entry ---------- */
  document.querySelectorAll("[data-gas-scene]").forEach(function (scene) {
    var fills = scene.querySelectorAll("[data-gas-fill]");
    var pctEl = scene.querySelector("[data-gas-pct]");
    var capEl = scene.querySelector("[data-gas-caption]");
    var barEl = scene.querySelector("[data-gas-bar]");

    var startLevel = (parseFloat(scene.dataset.startLevel) || 100) / 100;
    var endLevel = (parseFloat(scene.dataset.endLevel) || 30) / 100;
    var duration = parseFloat(scene.dataset.duration) || 2.6;

    /* captions: "70:Full and ready|35:Weeks of cooking left|12:Running low|0:Time to reorder" */
    var captions = (scene.dataset.captions || "")
      .split("|")
      .map(function (pair) {
        var i = pair.indexOf(":");
        if (i === -1) return null;
        return [parseFloat(pair.slice(0, i)), pair.slice(i + 1)];
      })
      .filter(Boolean)
      .sort(function (a, b) { return b[0] - a[0]; });

    function setLevel(level) {
      var pct = Math.round(level * 100);
      fills.forEach(function (f) {
        var wTop = parseFloat(f.dataset.top);
        var wBot = parseFloat(f.dataset.bot);
        /* surface position travels linearly from the topmost window's top (level=1)
           to the bottommost window's bottom (level=0) across the whole scene span */
        var frac;
        var surface = sceneSurface(scene, level);
        if (surface <= wTop) frac = 1;
        else if (surface >= wBot) frac = 0;
        else frac = (wBot - surface) / (wBot - wTop);
        f.style.height = (frac * 100).toFixed(1) + "%";
      });
      if (pctEl) pctEl.textContent = pct;
      if (barEl) barEl.style.transform = "scaleX(" + Math.max(0, Math.min(1, level)) + ")";
      if (capEl) {
        for (var i = 0; i < captions.length; i++) {
          if (pct >= captions[i][0]) {
            if (capEl.textContent !== captions[i][1]) capEl.textContent = captions[i][1];
            break;
          }
        }
      }
    }

    /* compute the overall drain span (top of first window -> bottom of last window) once */
    var spanTop = Infinity, spanBot = -Infinity;
    fills.forEach(function (f) {
      spanTop = Math.min(spanTop, parseFloat(f.dataset.top));
      spanBot = Math.max(spanBot, parseFloat(f.dataset.bot));
    });
    if (!isFinite(spanTop)) { spanTop = 0; spanBot = 100; }

    function sceneSurface(sc, level) {
      return spanTop + (1 - level) * (spanBot - spanTop);
    }

    setLevel(startLevel);

    if (!fills.length) return;

    if (hasGSAP && !reduced) {
      var state = { level: startLevel };
      gsap.timeline({
        scrollTrigger: { trigger: scene, start: "top 60%", toggleActions: "play none none reset" }
      }).fromTo(
        state,
        { level: startLevel },
        {
          level: endLevel,
          duration: duration,
          ease: "power2.inOut",
          onUpdate: function () { setLevel(state.level); },
          onReverseComplete: function () { setLevel(startLevel); }
        }
      );
    } else {
      /* CSS/IO fallback: rAF-tween 100 -> 30 each time the scene enters, reset on exit above it */
      var raf = null;
      var t0 = null;
      var animating = false;

      function easeInOutQuad(x) {
        return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
      }

      function step(now) {
        if (t0 === null) t0 = now;
        var p = Math.min(1, (now - t0) / (duration * 1000));
        var eased = easeInOutQuad(p);
        setLevel(startLevel + (endLevel - startLevel) * eased);
        if (p < 1) {
          raf = requestAnimationFrame(step);
        } else {
          animating = false;
        }
      }

      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              if (!animating) {
                animating = true;
                t0 = null;
                cancelAnimationFrame(raf);
                raf = requestAnimationFrame(step);
              }
            } else if (entry.boundingClientRect.top > 0) {
              /* scrolled back above the scene — reset so it replays on re-entry */
              cancelAnimationFrame(raf);
              animating = false;
              t0 = null;
              setLevel(startLevel);
            }
          });
        },
        { threshold: 0, rootMargin: "-40% 0px -40% 0px" }
      );
      io.observe(scene);
    }
  });

  /* ---------- bgc-wear — rAF-throttled drag divider ---------- */
  document.querySelectorAll("[data-wear]").forEach(function (wear) {
    /* walk to the nearest <section> ancestor rather than matching by class —
       the drag box and its wrapping section intentionally share the
       "bgc-wear" prefix, so a class-based closest() lookup would resolve
       to the drag box itself and miss the punch line. */
    var section = wear.closest("section");
    var punch = section ? section.querySelector("[data-wear-punch]") : null;
    var dragged = false;
    var down = false;
    var latestX = null;
    var rafPending = false;

    function applyX() {
      rafPending = false;
      if (latestX === null) return;
      var r = wear.getBoundingClientRect();
      var p = ((latestX - r.left) / r.width) * 100;
      p = Math.max(4, Math.min(96, p));
      wear.style.setProperty("--cut", p + "%");
      if (!dragged && Math.abs(p - 50) > 22) {
        dragged = true;
        if (punch) punch.classList.add("show");
      }
    }

    function setCut(clientX) {
      latestX = clientX;
      if (!rafPending) {
        rafPending = true;
        requestAnimationFrame(applyX);
      }
    }

    wear.addEventListener("pointerdown", function (e) {
      down = true;
      wear.setPointerCapture(e.pointerId);
      setCut(e.clientX);
    });
    wear.addEventListener("pointermove", function (e) { if (down) setCut(e.clientX); });
    wear.addEventListener("pointerup", function () { down = false; });
    wear.addEventListener("pointercancel", function () { down = false; });

    if (hasGSAP && !reduced) {
      ScrollTrigger.create({
        trigger: wear,
        start: "top 70%",
        once: true,
        onEnter: function () {
          var st = { p: 50 };
          gsap.to(st, {
            p: 44,
            yoyo: true,
            repeat: 1,
            duration: 0.8,
            ease: "power2.inOut",
            onUpdate: function () {
              if (!down && !dragged) wear.style.setProperty("--cut", st.p + "%");
            }
          });
        }
      });
    }
  });

  /* ---------- bgc-weigh — the approved interactive drag ---------- */
  document.querySelectorAll("[data-weigh-stage]").forEach(function (stage) {
    var rigs = Array.prototype.slice.call(stage.querySelectorAll("[data-weigh-rig]"));

    rigs.forEach(function (rig) {
      var handle = rig.querySelector("[data-weigh-handle]");
      var cap = rig.querySelector("[data-weigh-cap]");
      if (!handle) return;

      var isSteel = rig.dataset.weighRig === "steel";
      var dragRatio = parseFloat(rig.dataset.dragRatio) || (isSteel ? 0.13 : 1);
      var maxUp = parseFloat(rig.dataset.maxUp) || (isSteel ? 30 : 120);
      var dragThreshold = parseFloat(rig.dataset.dragThreshold) || (isSteel ? 14 : 40);
      var capIdle = cap ? (cap.dataset.capIdle || cap.textContent) : "";
      var capDrag = cap ? (cap.dataset.capDrag || "") : "";

      var pointerId = null;
      var startY = 0;
      var latestOffset = 0; /* raw pointer delta, negative = dragged up */
      var appliedY = 0;
      var appliedRot = 0;
      var rafPending = false;
      var wobbleT = 0;
      var releasing = false;

      function setCaption(dragging) {
        if (!cap) return;
        var next = dragging ? capDrag : capIdle;
        if (cap.textContent !== next) cap.textContent = next;
      }

      function applyTransform() {
        rafPending = false;
        handle.style.transform = "translateY(" + appliedY.toFixed(2) + "px) rotate(" + appliedRot.toFixed(2) + "deg)";
      }

      function queueApply() {
        if (!rafPending) {
          rafPending = true;
          requestAnimationFrame(applyTransform);
        }
      }

      function onPointerMove(e) {
        if (pointerId === null || e.pointerId !== pointerId) return;
        var dy = e.clientY - startY; /* negative when moving up */
        var up = Math.max(0, -dy);
        var travel = Math.min(maxUp, up * dragRatio);
        appliedY = -travel;

        if (isSteel) {
          wobbleT += 1;
          appliedRot = Math.sin(wobbleT * 0.35) * 1.5;
        } else {
          appliedRot = 0;
        }

        latestOffset = travel;
        setCaption(travel > dragThreshold * dragRatio || (isSteel && up > dragThreshold) || (!isSteel && travel > dragThreshold));
        queueApply();
      }

      function onPointerUp(e) {
        if (pointerId === null || (e && e.pointerId !== pointerId)) return;
        pointerId = null;
        rig.classList.remove("dragging");
        handle.classList.remove("grabbing");
        try { handle.releasePointerCapture(e.pointerId); } catch (err) {}
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);
        setCaption(false);
        releaseHandle();
      }

      function releaseHandle() {
        if (releasing) return;
        releasing = true;
        if (hasGSAP && !reduced) {
          if (isSteel) {
            /* heavy fast drop + tiny bounce */
            var tl = gsap.timeline({
              onComplete: function () { releasing = false; appliedY = 0; appliedRot = 0; }
            });
            tl.to(handle, {
              y: 6,
              rotate: 0,
              duration: 0.16,
              ease: "power4.in",
              onUpdate: function () {
                var y = gsap.getProperty(handle, "y");
                var r = gsap.getProperty(handle, "rotate");
                appliedY = y; appliedRot = r;
              }
            }).to(handle, {
              y: 0,
              duration: 0.22,
              ease: "back.out(3)",
              onUpdate: function () {
                var y = gsap.getProperty(handle, "y");
                appliedY = y;
              }
            });
          } else {
            /* BGC: soft power4 float back */
            gsap.to(handle, {
              y: 0,
              duration: 0.7,
              ease: "power4.out",
              onUpdate: function () {
                var y = gsap.getProperty(handle, "y");
                appliedY = y;
              },
              onComplete: function () { releasing = false; appliedY = 0; }
            });
          }
        } else {
          handle.style.transition = isSteel
            ? "transform 0.32s cubic-bezier(.36,1.6,.4,1)"
            : "transform 0.55s cubic-bezier(0.16,1,0.3,1)";
          appliedY = 0;
          appliedRot = 0;
          applyTransform();
          setTimeout(function () {
            handle.style.transition = "";
            releasing = false;
          }, 600);
        }
      }

      function onPointerDown(e) {
        if (releasing && hasGSAP) gsap.killTweensOf(handle);
        releasing = false;
        pointerId = e.pointerId;
        startY = e.clientY;
        wobbleT = 0;
        rig.classList.add("dragging");
        handle.classList.add("grabbing");
        try { handle.setPointerCapture(e.pointerId); } catch (err) {}
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
        window.addEventListener("pointercancel", onPointerUp);
        e.preventDefault();
      }

      handle.style.touchAction = "none";
      handle.addEventListener("pointerdown", onPointerDown);

      /* keyboard fallback: Enter/Space nudges up then releases, for accessibility */
      handle.addEventListener("keydown", function (e) {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        rig.classList.add("dragging");
        setCaption(true);
        appliedY = -Math.min(maxUp, isSteel ? 20 : 80);
        applyTransform();
        setTimeout(function () {
          rig.classList.remove("dragging");
          releaseHandle();
        }, 380);
      });
    });
  });

  /* ---------- bgc-safety: no bespoke JS required — reveal handled by global [data-rv] system ---------- */
})();
